import React, { useMemo } from 'react';

import ResultsPlot from '../../../encapsulation/components/ResultsPlot';

export const DEFAULT_ENCAP_PARAMS = {
  prominence: 0.1,
  width_factor: 2.0,
};

function getStatusClass(status) {
  if (status === 'running') return 'text-yellow-600';
  if (status === 'completed') return 'text-green-600';
  if (status === 'preview') return 'text-sky-600';
  return 'text-red-600';
}

export function EncapsulationRunView({
  sampleName = '—',
  showSampleSummary = true,
  executionSetup = null,
  status = '—',
  encapsulationEfficiency = null,
  totalRegion = null,
  totalFit = null,
  freeRegion = null,
  freeFit = null,
  failureMessage = null,
  params = null,
  footerNote = null,
}) {
  const prominence = params?.prominence ?? DEFAULT_ENCAP_PARAMS.prominence;
  const widthFactor = params?.width_factor ?? DEFAULT_ENCAP_PARAMS.width_factor;

  const totalData = useMemo(() => {
    if (!Array.isArray(totalRegion) || !Array.isArray(totalFit)) return null;
    return totalRegion.map((pt, i) => ({
      ...pt,
      fitted: totalFit[i],
    }));
  }, [totalFit, totalRegion]);

  const freeData = useMemo(() => {
    if (!Array.isArray(freeRegion) || !Array.isArray(freeFit)) return null;
    return freeRegion.map((pt, i) => ({
      ...pt,
      fitted: freeFit[i],
    }));
  }, [freeFit, freeRegion]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
      <aside className="space-y-6">
        {showSampleSummary && (
          <div className="p-6 bg-white rounded-xl shadow">
            <h3 className="text-lg font-semibold mb-2">Sample</h3>
            <div className="text-xl">{sampleName || '—'}</div>
            <div className="mt-2 text-sm">
              <span className="font-medium">Status:</span>{' '}
              <span className={getStatusClass(status)}>
                {status}
              </span>
            </div>
          </div>
        )}

        <div className="p-6 bg-white rounded-xl shadow">
          <h3 className="font-medium mb-2">Frozen Execution Setup</h3>
          {executionSetup ? (
            <ul className="text-sm space-y-1 text-gray-700">
              <li className="font-medium">
                {executionSetup.setup_name || executionSetup.name || 'Frozen setup snapshot'}
              </li>
              {executionSetup.source && (
                <li>Source: {executionSetup.source}</li>
              )}
              {executionSetup.temperature_k != null && (
                <li>
                  Temperature: {executionSetup.temperature_k.toFixed(2)} K (
                  {(executionSetup.temperature_k - 273.15).toFixed(1)} °C)
                </li>
              )}
              {executionSetup.viscosity_pa_s != null && (
                <li>Viscosity: {executionSetup.viscosity_pa_s} Pa·s</li>
              )}
              {executionSetup.capillary_diam_m != null && (
                <li>
                  Capillary diameter: {(executionSetup.capillary_diam_m * 1e6).toFixed(1)} µm
                </li>
              )}
              {executionSetup.eff_cap_length_m != null && (
                <li>
                  Effective length: {(executionSetup.eff_cap_length_m * 100).toFixed(1)} cm
                </li>
              )}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">
              No frozen execution setup was recorded for this run.
            </p>
          )}
        </div>

        <div className="p-6 bg-white rounded-xl shadow space-y-4">
          <h3 className="font-medium">Parameters</h3>
          <div>
            <label className="block text-sm">Prominence: {prominence.toFixed(2)}</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={prominence}
              onChange={() => {}}
              disabled
            />
          </div>
          <div>
            <label className="block text-sm">Width: {widthFactor.toFixed(2)}</label>
            <input
              type="range"
              min="0.5"
              max="5"
              step="0.1"
              value={widthFactor}
              onChange={() => {}}
              disabled
            />
          </div>
          {footerNote && (
            <p className="text-xs text-slate-500">{footerNote}</p>
          )}
        </div>

        {encapsulationEfficiency != null && (
          <div className="p-6 bg-indigo-50 rounded-xl shadow text-center">
            <div className="text-sm text-indigo-700">Encapsulation Efficiency</div>
            <div className="text-3xl font-extrabold text-indigo-900">
              {encapsulationEfficiency.toFixed(2)}%
            </div>
          </div>
        )}
      </aside>

      <main className="lg:col-span-3 space-y-6">
        {totalData ? (
          <>
            <ResultsPlot data={totalData} title="Total Peak Fit" />
            {freeData && <ResultsPlot data={freeData} title="Free Peak Fit" />}
          </>
        ) : status === 'failed' ? (
          <div className="text-red-600">
            {failureMessage || 'Analysis failed.'}
          </div>
        ) : (
          <div className="text-gray-500">Waiting for results…</div>
        )}
      </main>
    </div>
  );
}
